// src/pages/FindSalons.jsx

import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { searchSalons } from "../api/salons";

import { WORKSPACE_TYPES } from "../constants/workspaceTypes";
import { resolveAssetUrl } from "../utils/assetUrl";



const fallbackPhoto =

  "https://images.unsplash.com/photo-1507537509458-b8312d35a233?auto=format&fit=crop&w=1200&q=80";



// Filter options = "Any type" + all concrete types

const workspaceTypeFilterOptions = [

  { label: "Any type", value: "" },

  ...WORKSPACE_TYPES,

];



// Salon-level amenities (what you already had)

const amenityOptions = [

  "WiFi",

  "Parking",

  "Washer",

  "Dryer",

  "Reception",

  "Backbar",

];



// NEW: booth add-on amenity options (dynamic pricing targets)

const addonAmenityOptions = [

  { label: "Waxing room", key: "wax_room" },

  { label: "Pedicure chair", key: "pedi_chair" },

  { label: "Washer & dryer", key: "washer_dryer" },

  { label: "Nail polish use", key: "nail_polish" },

  { label: "Towel service", key: "towel_service" },

];



// Helper: today's date as YYYY-MM-DD

function getLocalTodayString() {

  const now = new Date();

  const y = now.getFullYear();

  const m = String(now.getMonth() + 1).padStart(2, "0");

  const d = String(now.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;

}



// Generate all 15-minute time slots

function generateTimeSlots() {

  const slots = [];

  for (let h = 0; h < 24; h++) {

    for (let m = 0; m < 60; m += 15) {

      const hour = h % 12 === 0 ? 12 : h % 12;

      const minute = String(m).padStart(2, "0");

      const ampm = h < 12 ? "AM" : "PM";

      const label = `${String(hour).padStart(2, "0")}:${minute} ${ampm}`;

      const value = `${String(h).padStart(2, "0")}:${minute}`;

      slots.push({ label, value });

    }

  }

  return slots;

}



// Custom Time Picker Component

function TimePicker({ value, onChange, date }) {

  const [open, setOpen] = useState(false);

  const today = getLocalTodayString();

  const slots = useMemo(() => generateTimeSlots(), []);



  const now = new Date();

  const currentTimeHHMM = now.toTimeString().slice(0, 5); // "HH:MM"



  const filteredSlots =

    date === today

      ? slots.filter((slot) => slot.value >= currentTimeHHMM)

      : slots;



  return (

    <div className="relative">

      <button

        type="button"

        onClick={() => setOpen(!open)}

        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left bg-white"

      >

        {value

          ? slots.find((s) => s.value === value)?.label

          : "Select time"}

      </button>



      {open && (

        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl">

          {filteredSlots.map((slot) => (

            <div

              key={slot.value}

              onClick={() => {

                onChange(slot.value);

                setOpen(false);

              }}

              className="px-4 py-2 cursor-pointer hover:bg-emerald-50 text-slate-700"

            >

              {slot.label}

            </div>

          ))}

        </div>

      )}

    </div>

  );

}



export default function FindSalons() {

  const navigate = useNavigate();

  const [salons, setSalons] = useState([]);

  const [loading, setLoading] = useState(false);



  const [query, setQuery] = useState("");

  const [location, setLocation] = useState("");

  const [type, setType] = useState("");

  const [minPrice, setMinPrice] = useState("");

  const [maxPrice, setMaxPrice] = useState("");

  const [date, setDate] = useState("");

  const [time, setTime] = useState("");



  // Preset salon amenities via checkboxes

  const [selectedAmenities, setSelectedAmenities] = useState([]);



  // custom amenity filters typed by the user

  const [customAmenityFilters, setCustomAmenityFilters] = useState([]);

  const [customAmenityInput, setCustomAmenityInput] = useState("");



  // NEW: selected booth add-on amenity keys

  const [selectedAddonKeys, setSelectedAddonKeys] = useState([]);



  const toggleAmenity = (amenity) => {

    setSelectedAmenities((prev) =>

      prev.includes(amenity)

        ? prev.filter((a) => a !== amenity)

        : [...prev, amenity]

    );

  };



  const addCustomAmenityFilter = () => {

    const trimmed = customAmenityInput.trim();

    if (!trimmed) return;

    if (!customAmenityFilters.includes(trimmed)) {

      setCustomAmenityFilters((prev) => [...prev, trimmed]);

    }

    setCustomAmenityInput("");

  };



  const removeCustomAmenityFilter = (name) => {

    setCustomAmenityFilters((prev) => prev.filter((a) => a !== name));

  };



  // NEW: toggle for add-on amenities

  const toggleAddonKey = (key) => {

    setSelectedAddonKeys((prev) =>

      prev.includes(key)

        ? prev.filter((k) => k !== key)

        : [...prev, key]

    );

  };



  const fetchSalons = async () => {

    setLoading(true);

    try {

      const params = {};

      if (query) params.q = query;

      if (location) params.city = location;

      if (type) params.type = type;

      if (minPrice !== "") params.minPrice = Number(minPrice);

      if (maxPrice !== "") params.maxPrice = Number(maxPrice);

      if (date) params.date = date;

      if (time) params.start_time = time;



      // Combine preset + custom amenity filters

      const allAmenityFilters = [

        ...selectedAmenities,

        ...customAmenityFilters,

      ];

      if (allAmenityFilters.length) {

        params.amenities = allAmenityFilters.join(",");

      }



      // NEW: add-on amenity filters (booth-level)

      // Backend can later use this to ensure at least one booth in the salon

      // offers all selected add-ons (wax_room, pedi_chair, etc.).

      if (selectedAddonKeys.length) {

        params.addon_keys = selectedAddonKeys.join(",");

      }



      const data = await searchSalons(params);

      setSalons(data);

    } catch (err) {

      console.error("Failed to search salons", err);

      setSalons([]);

    } finally {

      setLoading(false);

    }

  };



  // Initial unfiltered load

  useEffect(() => {

    let isMounted = true;



    const loadInitialSalons = async () => {

      setLoading(true);

      try {

        const data = await searchSalons({});

        if (isMounted) {

          setSalons(data);

        }

      } catch (err) {

        console.error("Failed to load salons", err);

        if (isMounted) {

          setSalons([]);

        }

      } finally {

        if (isMounted) {

          setLoading(false);

        }

      }

    };



    loadInitialSalons();



    return () => {

      isMounted = false;

    };

  }, []);



  // Group by city for nicer layout

  const groupedByCity = useMemo(() => {

    const groups = {};

    for (const salon of salons) {

      const city = salon.city || "Other locations";

      if (!groups[city]) groups[city] = [];

      groups[city].push(salon);

    }

    return groups;

  }, [salons]);



  const hasFilters =

    query ||

    location ||

    type ||

    minPrice !== "" ||

    maxPrice !== "" ||

    date ||

    time ||

    selectedAmenities.length > 0 ||

    customAmenityFilters.length > 0 ||

    selectedAddonKeys.length > 0;



  return (

    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">

        {/* HEADER */}

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">

          <div>

            <p className="text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold">

              ELLYANNA BOOTHSHARE

            </p>

            <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mt-2">

              Find your next salon workspace

            </h1>

            <p className="text-slate-600 mt-2 max-w-2xl">

              Find salons, choose your workspace, and book in minutes.

              Filter by availability, price, amenities, and add-on

              amenities like waxing rooms or pedicure chairs.

            </p>

          </div>

        </div>



        {/* FILTERS SECTION */}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3">

          {/* LOCATION */}

          <div className="md:col-span-2">

            <label className="text-xs font-semibold text-slate-500">

              Location

            </label>

            <input

              type="text"

              placeholder="City or neighborhood"

              value={location}

              onChange={(e) => setLocation(e.target.value)}

              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"

            />

          </div>



          {/* DATE */}

          <div>

            <label className="text-xs font-semibold text-slate-500">

              Date

            </label>

            <input

              type="date"

              value={date}

              min={getLocalTodayString()}

              onChange={(e) => setDate(e.target.value)}

              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"

            />

          </div>



          {/* KEYWORD SEARCH */}

          <div>

            <label className="text-xs font-semibold text-slate-500">

              Search

            </label>

            <input

              type="text"

              placeholder="Salon name, area, or keyword"

              value={query}

              onChange={(e) => setQuery(e.target.value)}

              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"

            />

          </div>



          {/* WORKSPACE TYPE */}

          <div>

            <label className="text-xs font-semibold text-slate-500">

              Workspace Type

            </label>

            <select

              value={type}

              onChange={(e) => setType(e.target.value)}

              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"

            >

              {workspaceTypeFilterOptions.map((opt) => (

                <option key={opt.value || "any"} value={opt.value}>

                  {opt.label}

                </option>

              ))}

            </select>

          </div>

        </div>



        {/* SECOND ROW: TIME + PRICE + AMENITIES */}

        <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">

          {/* TIME — CUSTOM PICKER */}

          <div>

            <label className="text-xs font-semibold text-slate-500">

              Start time

            </label>



            <TimePicker

              value={time}

              date={date}

              onChange={(newTime) => setTime(newTime)}

            />

          </div>



          {/* PRICE RANGE */}

          <div className="md:col-span-2">

            <label className="text-xs font-semibold text-slate-500">

              Price range (per hour)

            </label>

            <div className="mt-1 grid grid-cols-2 gap-2">

              <div>

                <span className="block text-[11px] text-slate-400 mb-0.5">

                  Min

                </span>

                <input

                  type="number"

                  min="0"

                  placeholder="20"

                  value={minPrice}

                  onChange={(e) => setMinPrice(e.target.value)}

                  className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3"

                />

              </div>

              <div>

                <span className="block text-[11px] text-slate-400 mb-0.5">

                  Max

                </span>

                <input

                  type="number"

                  min="0"

                  placeholder="300"

                  value={maxPrice}

                  onChange={(e) => setMaxPrice(e.target.value)}

                  className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3"

                />

              </div>

            </div>

          </div>



          {/* AMENITIES + ADD-ON AMENITIES */}

          <div className="md:col-span-2">

            <label className="text-xs font-semibold text-slate-500">

              Amenities

            </label>



            {/* Preset salon amenity chips */}

            <div className="mt-1 flex flex-wrap gap-2">

              {amenityOptions.map((amenity) => {

                const active = selectedAmenities.includes(amenity);

                return (

                  <button

                    key={amenity}

                    type="button"

                    onClick={() => toggleAmenity(amenity)}

                    className={`px-3 py-1 rounded-full text-xs border ${

                      active

                        ? "bg-emerald-600 text-white border-emerald-600"

                        : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"

                    }`}

                  >

                    {amenity}

                  </button>

                );

              })}

            </div>



            {/* Custom amenity filters */}

            <div className="mt-2 flex gap-2">

              <input

                type="text"

                placeholder="Type an amenity (e.g. massage chair)"

                value={customAmenityInput}

                onChange={(e) => setCustomAmenityInput(e.target.value)}

                onKeyDown={(e) => {

                  if (e.key === "Enter") {

                    e.preventDefault();

                    addCustomAmenityFilter();

                  }

                }}

                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 outline-none"

              />

              <button

                type="button"

                onClick={addCustomAmenityFilter}

                className="px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"

              >

                Add

              </button>

            </div>



            {/* Display active custom filters */}

            {customAmenityFilters.length > 0 && (

              <div className="mt-2 flex flex-wrap gap-2">

                {customAmenityFilters.map((name) => (

                  <span

                    key={name}

                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-[11px] text-slate-700"

                  >

                    {name}

                    <button

                      type="button"

                      onClick={() => removeCustomAmenityFilter(name)}

                      className="text-slate-500 hover:text-slate-800"

                    >

                      ×

                    </button>

                  </span>

                ))}

              </div>

            )}



            {/* NEW: Add-on amenity filters (booth-level) */}

            <div className="mt-3">

              <p className="text-[11px] font-semibold text-slate-500 mb-1">

                Add-on amenities (booth level)

              </p>

              <div className="flex flex-wrap gap-2">

                {addonAmenityOptions.map((opt) => {

                  const active = selectedAddonKeys.includes(opt.key);

                  return (

                    <button

                      key={opt.key}

                      type="button"

                      onClick={() => toggleAddonKey(opt.key)}

                      className={`px-3 py-1 rounded-full text-[11px] border ${

                        active

                          ? "bg-violet-600 text-white border-violet-600"

                          : "bg-white text-slate-700 border-slate-200 hover:border-violet-400"

                      }`}

                    >

                      {opt.label}

                    </button>

                  );

                })}

              </div>

            </div>

          </div>

        </div>



        {/* SEARCH BUTTON + CLEAR */}

        <div className="mt-4 flex flex-wrap items-center gap-3">

          <button

            type="button"

            onClick={fetchSalons}

            className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm shadow-emerald-100"

          >

            Search salons

          </button>

          {hasFilters && (

            <button

              type="button"

              onClick={() => {

                setQuery("");

                setLocation("");

                setType("");

                setMinPrice("");

                setMaxPrice("");

                setDate("");

                setTime("");

                setSelectedAmenities([]);

                setCustomAmenityFilters([]);

                setCustomAmenityInput("");

                setSelectedAddonKeys([]);

                fetchSalons();

              }}

              className="text-xs text-slate-500 hover:text-slate-800 underline"

            >

              Clear all filters

            </button>

          )}

        </div>



        {/* RESULTS */}

        <div className="mt-8">

          {loading ? (

            <div className="py-10 text-center text-slate-500">

              Searching salons...

            </div>

          ) : salons.length === 0 ? (

            <div className="py-10 text-center text-slate-500">

              No salons found. Try adjusting your filters.

            </div>

          ) : (

            Object.entries(groupedByCity).map(([city, citySalons]) => (

              <div key={city} className="mb-8">

                <h2 className="text-lg font-semibold text-slate-900 mb-3">

                  {city}

                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

                  {citySalons.map((salon) => (

                    <SalonCard

                      key={salon.id}

                      salon={salon}

                      onClick={() => navigate(`/salons/${salon.id}`)}

                    />

                  ))}

                </div>

              </div>

            ))

          )}

        </div>

      </div>

    </div>

  );

}



function prefix(url) {

  if (!url) return url;

  return url.startsWith("/static")

    ? resolveAssetUrl(url)

    : url;

}



function SalonCard({ salon, onClick }) {

  const rawPhoto = salon.first_photo || fallbackPhoto;

  const photo = prefix(rawPhoto);

  const price = salon.min_price

    ? `$${salon.min_price.toFixed(0)} / hour`

    : "Price coming soon";



  return (

    <div

      onClick={onClick}

      className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition cursor-pointer overflow-hidden flex flex-col"

    >

      <div className="h-40 w-full bg-slate-100 overflow-hidden">

        <img

          src={photo}

          alt={salon.name}

          className="w-full h-full object-cover"

        />

      </div>

      <div className="p-4 flex-1 flex flex-col justify-between">

        <div>

          <h3 className="text-lg font-semibold text-slate-900">

            {salon.name}

          </h3>

          <p className="text-sm text-gray-600">

            {salon.city || "City coming soon"}

          </p>

          <p className="text-sm text-gray-900 font-semibold">

            {price}

          </p>

        </div>

      </div>

    </div>

  );

}

